import { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { connectToHubProduct } from "src/redux/slices/hub";
import GetUrlApi from "../UrlApi";
import {
  getFrequentProducts,
  getProducts,
  deleteProductSuccess,
} from "src/redux/slices/product";
import { planProductHubRefresh } from "../tpvSyncCore";
import CreateConnection from "./HubMatrix";
import { getCategories, getTags } from "src/redux/slices/categories";

// URL del hub con ?restauranteId=... para que el server una la conexión al grupo
// "restaurante-{id}" en OnConnectedAsync. Así los broadcasts viajan SOLO al
// restaurante correcto (no cross-tenant).
function buildProductHubUrl(restauranteId) {
  const base = `${GetUrlApi()}/hub/producthub`;
  return restauranteId ? `${base}?restauranteId=${restauranteId}` : base;
}

/**
 * Hub de productos unificado. Escucha `products/restaurante/{id}` y reacciona al payload:
 *   { idRestaurante, productoId, action, timestamp }
 *
 * Acciones soportadas:
 *   - "added"        → refetch productos (el backend no devuelve aún el DTO en payload).
 *   - "updated"      → refetch productos.
 *   - "deleted"      → refetch productos.
 *   - "activated"    → refetch productos.
 *   - "availability" → refetch productos (cambió disponibilidad).
 *   - null/"bulk"    → refetch completo (categorías, tags, frequent) porque viene de
 *     una edición de categoría u otra operación masiva.
 *
 * Antes había 2 hooks (`useProductSessionHub` + `useProductUserHub`) que se
 * registraban a eventos duplicados → doble dispatch por evento. Ahora uno solo.
 *
 * Futura optimización: cuando el backend devuelva el DTO del producto en el
 * payload, aquí se podría hacer un patch fino en Redux por productoId (upsert
 * / remove) sin refetch. Dejo el scaffolding para cuando se implemente.
 */
export function useProductsHub() {
  const dispatch = useDispatch();
  const { user, session } = useSelector((state) => state.authJwt);
  const isConnectedRef = useRef(false);

  // restauranteId viene del user (admin/camarero) o de la session (QR comensal).
  const restauranteId = user?.id || session?.idRestaurante;

  // Fase 3 · Eventos agrupados (ventana 400 ms): una importación o una
  // edición masiva emite decenas → antes N recargas del catálogo (1,3 MB)
  // en cada dispositivo. Ahora: borrados → parche local sin recarga; resto
  // → UNA recarga de productos; categorías/tags/frecuentes solo si hubo un
  // cambio masivo (bulk o sin acción).
  const pendingRef = useRef({ timer: null, events: [] });

  const flush = useCallback(() => {
    const events = pendingRef.current.events;
    pendingRef.current = { timer: null, events: [] };
    const plan = planProductHubRefresh(events);
    plan.deletedIds.forEach((id) => dispatch(deleteProductSuccess(id)));
    // FORCE: los thunks llevan staleGuard (TTL 5 min); un evento SignalR es
    // un aviso explícito de cambio, el guardián no aplica. Fix 2026-07-10.
    if (plan.refetchProducts) dispatch(getProducts(undefined, { force: true }));
    if (plan.refetchCatalogExtras) {
      dispatch(getCategories(undefined, { force: true }));
      dispatch(getFrequentProducts());
      dispatch(getTags(false, { force: true }));
    }
  }, [dispatch]);

  const handler = useCallback(
    (payload) => {
      pendingRef.current.events.push(payload || {});
      if (!pendingRef.current.timer) {
        pendingRef.current.timer = setTimeout(flush, 400);
      }
    },
    [flush],
  );

  useEffect(() => {
    if (!restauranteId || isConnectedRef.current) return;

    isConnectedRef.current = true;

    const cleanup = CreateConnection(
      buildProductHubUrl(restauranteId),
      [
        {
          id: `products/restaurante/${restauranteId}`,
          operator: handler,
        },
      ],
      dispatch,
      connectToHubProduct,
      "product",
    );

    return () => {
      cleanup();
      isConnectedRef.current = false;
    };
  }, [restauranteId, dispatch, handler]);
}

// ---- Backward-compat ----
// `useProductSessionHub` y `useProductUserHub` eran dos hooks que escuchaban
// eventos distintos. Ahora hay UN solo evento `products/restaurante/{id}` y
// `useProductsHub` lo gestiona. Mantenemos los nombres antiguos como:
//  - useProductUserHub → el hook real (mantiene el nombre más común).
//  - useProductSessionHub → NO-OP para que callers antiguos no dupliquen
//    la conexión (llamar ambos en el mismo componente registraba 2 listeners
//    al mismo eventId → doble refetch).
export const useProductUserHub = useProductsHub;
export function useProductSessionHub() {
  /* no-op: el hub de productos ahora es unificado en useProductsHub.
     Mantener el export por compatibilidad con layouts existentes que
     lo llaman junto a useProductUserHub — así evitamos la duplicación. */
}
